import classNames from 'classnames'
import React from 'react'

import { ChipGroup, Drawer } from '@src/quince-components/src/atoms'

import {
  DRAWER_WIDTH,
  CLEAR_ALL_TEXT,
  DRAWER_FOOTER_VIEW_TEXT,
  DRAWER_TITLE_TEXT,
  CLEAR_ALL_THRESHOLD,
  DRAWER_FOOTER_CLEAR_TEXT,
} from '../constants/filterSection.constants'
import desktopStyles from '../menu/filterSectionWithDrawer/filterSectionWithDrawer.module.scss' // Desktop  styles
import styles from '../menu/mobileFilters/mobileFilters.module.scss' // Mobile styles

interface FilterChip {
  label: string
  filterType: string
  value: string
}

interface FilterDrawerProps {
  open: boolean
  onClose: () => void
  placement?: 'left' | 'right' | 'top' | 'bottom'
  title?: React.ReactNode
  width?: number | string
  height?: string | number
  headerClassName?: string
  bodyClassName?: string
  footerClassName?: string
  children: React.ReactNode
  selectedFilterChips?: FilterChip[]
  onChipDismiss?: (label: string) => void
  clearAllFilters?: () => void
  totalSelectedFilters: number
  noOfItems?: number
  customFooter?: React.ReactNode
  hideFooter?: boolean
  isMobile?: boolean
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  placement = 'left',
  title,
  width = DRAWER_WIDTH,
  height = 'auto',
  headerClassName,
  bodyClassName,
  footerClassName,
  children,
  selectedFilterChips = [],
  onChipDismiss,
  clearAllFilters,
  totalSelectedFilters,
  noOfItems = 0,
  customFooter,
  hideFooter = false,
  isMobile = true,
}) => {
  // Use the appropriate style set based on viewport
  const styleSet = isMobile ? styles : desktopStyles

  // Default title render function
  const renderDefaultTitle = () => (
    <div className={styleSet.drawerTitleContainer}>
      <div className={styleSet.drawerTitleText}>
        {DRAWER_TITLE_TEXT}
        {totalSelectedFilters > 0 && ` (${totalSelectedFilters})`}
      </div>
    </div>
  )

  // Footer render function
  const renderFooter = () => {
    if (hideFooter) {
      return null
    }

    if (customFooter) {
      return customFooter
    }

    return (
      <div className={classNames(styleSet.drawerFooter, footerClassName)}>
        <button
          className={classNames(
            styleSet.drawerClearAllButton,
            totalSelectedFilters === 0 ? styleSet.drawerClearAllButtonDisabled : '',
          )}
          onClick={e => {
            e.stopPropagation()
            clearAllFilters?.()
          }}
          disabled={totalSelectedFilters === 0}
        >
          {DRAWER_FOOTER_CLEAR_TEXT}
        </button>
        <button className={styleSet.drawerCloseButton} onClick={onClose}>
          {DRAWER_FOOTER_VIEW_TEXT} ({noOfItems})
        </button>
      </div>
    )
  }

  return (
    <Drawer
      placement={placement}
      open={open}
      onClose={onClose}
      title={title ?? renderDefaultTitle()}
      width={width}
      height={height}
      headerClassName={classNames(styleSet.drawerCustomHeader, headerClassName)}
      bodyClassName={classNames(styleSet.drawerBody, bodyClassName)}
      footer={renderFooter()}
    >
      <div
        className={classNames(styleSet.drawerChipsContainer, {
          [`${styleSet.noChipsContainer}`]: selectedFilterChips.length <= 0,
        })}
      >
        {selectedFilterChips.length > 0 && onChipDismiss && (
          <>
            <ChipGroup
              chips={selectedFilterChips.map(chip => ({
                label: chip.label,
                value: chip.value,
                closeable: true,
                key: `${chip.filterType}-${chip.value}`,
              }))}
              onChipDismiss={onChipDismiss}
              clearAll={false}
              className={styleSet.drawerTitleChips}
              chipClassName={styleSet.drawerFilterChip}
            />
            {!isMobile && selectedFilterChips.length >= CLEAR_ALL_THRESHOLD && clearAllFilters && (
              <button className={styleSet.drawerTitleClearAll} onClick={clearAllFilters}>
                {CLEAR_ALL_TEXT}
              </button>
            )}
          </>
        )}
      </div>
      <div className={styleSet.drawerContent}>{children}</div>
    </Drawer>
  )
}
